import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { uploadDocument, batchUploadDocuments } from "../lib/api";
import { useUpload } from "../lib/UploadContext";

const PIPELINE_STEPS = [
  "Document Downloaded", "Content Extracted", "Content Indexed",
  "QA Checks Completed", "QA Results Indexed", "Processing Complete"
];

function useElapsed(active) {
  const startRef = useRef(active ? Date.now() : null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    startRef.current = startRef.current || Date.now();
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
  if (!active || !startRef.current) return "";
  const s = Math.floor((Date.now() - startRef.current) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function SingleFileTracker({ single }) {
  const isDone  = single.status === "COMPLETE";
  const isError = single.status === "ERROR";
  const color   = isDone ? "var(--success)" : isError ? "var(--error)" : "var(--blue-500)";
  return (
    <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text-primary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
          {single.filename}
        </span>
        <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", fontWeight: 600, color, flexShrink: 0 }}>
          {isDone ? "✓ Complete" : isError ? "Error" : `${single.pct}%`}
        </span>
      </div>
      <div style={{ height: 5, background: "var(--gray-100)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${single.pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function BatchFileRow({ file, onNavigate }) {
  const isPassed     = file.status === "PASSED";
  const isFailed     = file.status === "FAILED";
  const isProcessing = !file.status || file.status === "PROCESSING";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 16px", borderBottom: "1px solid var(--border)" }}>
      <span style={{ flexShrink: 0, color: (isPassed || isFailed) ? "#10b981" : "var(--text-tertiary)" }}>
        {(isPassed || isFailed)
          ? <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6"/><polyline points="5 8 7 10 11 6"/></svg>
          : <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ animation: "spin 1s linear infinite" }}><path d="M8 2a6 6 0 1 0 6 6" strokeLinecap="round"/></svg>}
      </span>
      <span style={{ flex: 1, fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text-primary)",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {file.filename}
      </span>
      <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", flexShrink: 0 }}>
        {file.size ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : ""}
      </span>
      {(isPassed || isFailed) && <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", padding: "2px 7px", borderRadius: 4, flexShrink: 0 }}>Uploaded</span>}
      {isProcessing && <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", flexShrink: 0 }}>Processing…</span>}
    </div>
  );
}

export default function Upload() {
  const { accounts }  = useMsal();
  const uploaderName  = accounts?.[0]?.name || accounts?.[0]?.username || "Unknown";
  const navigate      = useNavigate();
  const { trackBatch, trackSingle, batches, singles } = useUpload();

  const [mode, setMode]         = useState("single");
  const [dragging, setDragging] = useState(false);
  const fileInputRef   = useRef(null);
  const folderInputRef = useRef(null);

  // webkitdirectory is non-standard — React strips it as a JSX prop.
  // Use a callback ref so the attribute is set the moment the element mounts.
  const folderRefCallback = useCallback((node) => {
    if (node) {
      node.setAttribute("webkitdirectory", "");
      node.setAttribute("directory", "");
      folderInputRef.current = node;
    }
  }, []);

  // Single state
  const [singleFiles, setSingleFiles] = useState([]);
  const [uploading, setUploading]     = useState(false);
  // Batch state
  const [batchFiles, setBatchFiles]   = useState([]);
  const [batchPhase, setBatchPhase]   = useState("staging");
  const [uploadPct, setUploadPct]     = useState(0);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [error, setError]             = useState(null);
  const [conflictFiles, setConflictFiles] = useState([]);

  const activeBatch = batches.find(b => b.batch_id === activeBatchId);
  const batchDone   = activeBatch?.status === "COMPLETE";
  const batchPct    = activeBatch ? (activeBatch.total > 0 ? Math.round((activeBatch.completed / activeBatch.total) * 100) : 0) : 0;
  const elapsed     = useElapsed(batchPhase === "active" && !batchDone);
  const trackedSingles = singles.filter(s => singleFiles.some(f => f.filename === s.filename));
  const allSingleDone  = singleFiles.length > 0 && singleFiles.every(f => f.status === "done" || f.status === "error");
  const fmtSize = n => n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
  const batchFileMap = Object.fromEntries((activeBatch?.files || []).map(f => [f.filename, f]));

  const addSingleFiles = useCallback((incoming) => {
    const pdfs = Array.from(incoming).filter(f => f.name.toLowerCase().endsWith(".pdf"));
    setSingleFiles(prev => {
      const existing = new Set(prev.map(f => f.id));
      return [...prev, ...pdfs.filter(f => !existing.has(`${f.name}-${f.size}`))
        .map(f => ({ file: f, id: `${f.name}-${f.size}`, filename: f.name, status: "pending", progress: 0 }))];
    });
  }, []);

  const addBatchFiles = useCallback((incoming) => {
    const pdfs = Array.from(incoming).filter(f => f.name.toLowerCase().endsWith(".pdf"));
    setBatchFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...pdfs.filter(f => !existing.has(f.name))];
    });
  }, []);

  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    if (mode === "batch") {
      const items = e.dataTransfer.items;
      const allFiles = [];
      let pending = 0;
      const done = () => { if (--pending === 0) addBatchFiles(allFiles); };
      for (const item of (items || [])) {
        const entry = item.webkitGetAsEntry?.();
        if (entry?.isDirectory) {
          pending++;
          const readDir = (r) => r.readEntries(entries => {
            if (!entries.length) { done(); return; }
            entries.forEach(e2 => e2.isFile ? e2.file(f => allFiles.push(f)) : null);
            readDir(r);
          });
          readDir(entry.createReader());
        } else if (entry?.isFile) {
          pending++;
          entry.file(f => { allFiles.push(f); done(); });
        }
      }
      if (pending === 0) addBatchFiles(Array.from(e.dataTransfer.files));
    } else {
      addSingleFiles(e.dataTransfer.files);
    }
  }, [mode, addBatchFiles, addSingleFiles]);

  const handleSingleUpload = async () => {
    const pending = singleFiles.filter(f => f.status === "pending");
    if (!pending.length) return;
    setUploading(true);
    for (const entry of pending) {
      setSingleFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "uploading" } : f));
      try {
        await uploadDocument(entry.file, pct => setSingleFiles(prev => prev.map(f => f.id === entry.id ? { ...f, progress: pct } : f)), uploaderName);
        setSingleFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "done", progress: 100 } : f));
        trackSingle(entry.filename);
      } catch {
        setSingleFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "error" } : f));
      }
    }
    setUploading(false);
  };

  const handleBatchUpload = async (force = false) => {
    if (!batchFiles.length) return;
    setError(null);
    setBatchPhase("uploading");
    const batchName = `Batch ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    try {
      const result = await batchUploadDocuments(batchFiles, batchName, uploaderName, pct => setUploadPct(pct), force);
      if (result.conflict) {
        setBatchPhase("staging");
        setConflictFiles(result.conflicting_files);
        return;
      }
      trackBatch(result, batchFiles);
      setActiveBatchId(result.batch_id);
      setBatchPhase("active");
    } catch (e) {
      setError(e.message);
      setBatchPhase("staging");
    }
  };

  const resetBatch = () => { setBatchFiles([]); setBatchPhase("staging"); setActiveBatchId(null); setError(null); setConflictFiles([]); };
  const showToggle = batchPhase === "staging" && !uploading && !trackedSingles.length;

  return (
    <>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">Upload Documents</div>
          <div className="page-subtitle">Upload single or multiple PDF files for QA processing</div>
        </div>
      </div>

      <div className="page-body" style={{ maxWidth: 720 }}>

        {/* Hidden file inputs — always in DOM so refs are valid regardless of mode/phase */}
        <input ref={fileInputRef}   type="file" multiple accept=".pdf" style={{ display: "none" }}
          onChange={e => mode === "batch" ? addBatchFiles(e.target.files) : addSingleFiles(e.target.files)} />
        <input ref={folderRefCallback} type="file" multiple style={{ display: "none" }}
          onChange={e => addBatchFiles(e.target.files)} />

        {/* Mode toggle */}
        {showToggle && (
          <div style={{ display: "flex", gap: 0, background: "var(--gray-100)", borderRadius: 8, padding: 3, marginBottom: 16, width: "fit-content" }}>
            {[["single", "Single File"], ["batch", "Batch Upload"]].map(([m, label]) => (
              <button key={m} onClick={() => { setMode(m); setSingleFiles([]); setBatchFiles([]); setError(null); }}
                style={{ padding: "6px 18px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500,
                  background: mode === m ? "var(--white)" : "transparent",
                  color: mode === m ? "var(--text-primary)" : "var(--text-tertiary)",
                  boxShadow: mode === m ? "0 1px 3px rgba(0,0,0,0.1)" : "none", transition: "all 0.15s" }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* ── SINGLE MODE ── */}
        {mode === "single" && (
          <>
            {!allSingleDone && !uploading && (
              <div className={`upload-zone${dragging ? " dragging" : ""}`}
                onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
                onClick={() => fileInputRef.current?.click()}>
                <div className="upload-icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1"/>
                    <polyline points="16 12 12 8 8 12" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="8" x2="12" y2="20"/>
                  </svg>
                </div>
                <div className="upload-title">{dragging ? "Release to add files" : "Drop PDFs here or click to browse"}</div>
                <div className="upload-subtitle">Select IOM report PDFs for QA validation</div>
                <div className="upload-types">PDF</div>
              </div>
            )}

            {singleFiles.length > 0 && (
              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-header">
                  <div className="card-title">Selected Files ({singleFiles.length})</div>
                  {!uploading && !allSingleDone && <button className="btn btn-ghost btn-sm" onClick={() => setSingleFiles([])}>Clear all</button>}
                </div>
                {singleFiles.map(f => (
                  <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 34, height: 34, borderRadius: 6, background: "var(--blue-50)", border: "1px solid var(--blue-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "var(--blue-500)" }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/><path d="M9 1v4h4"/></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 1 }}>{f.file.name}</div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>{fmtSize(f.file.size)}</div>
                      {f.status === "uploading" && <div className="progress-track" style={{ marginTop: 5 }}><div className="progress-fill" style={{ width: `${f.progress}%` }} /></div>}
                    </div>
                    <div style={{ flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 11 }}>
                      {f.status === "pending" && !uploading && <button className="btn btn-ghost btn-sm" onClick={() => setSingleFiles(prev => prev.filter(x => x.id !== f.id))}>✕</button>}
                      {f.status === "uploading" && <span style={{ color: "var(--blue-500)" }}>{f.progress}%</span>}
                      {f.status === "done"      && <span style={{ color: "var(--success)" }}>✓ Uploaded</span>}
                      {f.status === "error"     && <span style={{ color: "var(--error)" }}>Failed</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {singleFiles.length > 0 && !allSingleDone && (
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={handleSingleUpload} disabled={uploading}>
                  {uploading ? <><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }}><circle cx="8" cy="8" r="6" strokeOpacity=".25"/><path d="M8 2a6 6 0 016 6" strokeLinecap="round"/></svg> Uploading…</> : `Upload ${singleFiles.length} file${singleFiles.length > 1 ? "s" : ""}`}
                </button>
                {!uploading && <button className="btn btn-secondary" onClick={() => setSingleFiles([])}>Cancel</button>}
              </div>
            )}

            {trackedSingles.length > 0 && (
              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-header">
                  <div className="card-title">Processing Status</div>
                  <div className="card-subtitle">Documents appear in All Documents once fully complete</div>
                </div>
                {trackedSingles.map(s => <SingleFileTracker key={s.filename} single={s} />)}
                <div style={{ padding: "12px 16px", display: "flex", gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate("/documents")}>View Documents →</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSingleFiles([])}>Upload More</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── BATCH MODE ── */}
        {mode === "batch" && batchPhase === "staging" && (
          <>
            <div className={`upload-zone${dragging ? " dragging" : ""}`}
              onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
              style={{ cursor: "default" }}>
              <div className="upload-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1"/>
                  <polyline points="16 12 12 8 8 12" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="8" x2="12" y2="20"/>
                </svg>
              </div>
              <div className="upload-title">{dragging ? "Release to add files" : "Drop a folder or files here"}</div>
              <div className="upload-subtitle">Or use the buttons below to browse files or select a folder</div>
              <div className="upload-types">PDF</div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}>Select Files</button>
              <button className="btn btn-secondary btn-sm" onClick={() => folderInputRef.current?.click()}>Select Folder</button>
              {batchFiles.length > 0 && <button className="btn btn-secondary btn-sm" style={{ marginLeft: "auto", color: "var(--error)" }} onClick={() => setBatchFiles([])}>Clear All</button>}
            </div>
            {batchFiles.length > 0 && (
              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-header">
                  <div><div className="card-title">{batchFiles.length} PDF{batchFiles.length !== 1 ? "s" : ""} ready</div><div className="card-subtitle">Review before starting</div></div>
                  <button className="btn btn-primary btn-sm" onClick={handleBatchUpload}>Start Batch QA</button>
                </div>
                <div style={{ maxHeight: 400, overflowY: "auto" }}>
                  {batchFiles.map(f => (
                    <div key={f.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ flex: 1, fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", flexShrink: 0 }}>{fmtSize(f.size)}</span>
                      <button onClick={() => setBatchFiles(prev => prev.filter(x => x.name !== f.name))} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 2 }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {mode === "batch" && batchPhase === "uploading" && (
          <div className="card">
            <div className="card-header"><div className="card-title">Uploading {batchFiles.length} files…</div></div>
            <div className="card-body">
              <div style={{ height: 8, background: "var(--gray-100)", borderRadius: 4, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ width: `${uploadPct}%`, height: "100%", background: "var(--blue-500)", borderRadius: 4, transition: "width 0.3s ease" }} />
              </div>
              <div style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textAlign: "center" }}>{uploadPct}%</div>
            </div>
          </div>
        )}

        {mode === "batch" && batchPhase === "active" && activeBatch && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">{batchDone ? "Batch Complete" : "Processing Batch…"}</div>
                <div className="card-subtitle">{batchDone ? `${activeBatch.total} documents processed` : `${activeBatch.completed} of ${activeBatch.total} · ${elapsed} elapsed`}</div>
              </div>
              {batchDone && (
                <div style={{ display: "flex", gap: 16 }}>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 300, color: "var(--success)", lineHeight: 1 }}>{activeBatch.passed}</div><div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginTop: 2 }}>PASSED</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 300, color: activeBatch.failed > 0 ? "var(--error)" : "var(--text-tertiary)", lineHeight: 1 }}>{activeBatch.failed}</div><div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginTop: 2 }}>FAILED</div></div>
                </div>
              )}
            </div>
            <div style={{ padding: "0 20px 14px" }}>
              <div style={{ height: 8, background: "var(--gray-100)", borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ width: `${batchPct}%`, height: "100%", background: batchDone ? "var(--success)" : "var(--blue-500)", borderRadius: 4, transition: "width 0.5s ease" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>
                <span>{activeBatch.completed} / {activeBatch.total} complete</span><span>{batchPct}%</span>
              </div>
            </div>
            <div style={{ maxHeight: 460, overflowY: "auto", borderTop: "1px solid var(--border)" }}>
              {batchFiles.map(f => <BatchFileRow key={f.name} file={{ filename: f.name, size: f.size, ...(batchFileMap[f.name] || {}) }} onNavigate={fn => navigate(`/documents/${fn}`)} />)}
            </div>
            {batchDone && (
              <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 10, background: "var(--gray-50)" }}>
                <button className="btn btn-secondary btn-sm" onClick={resetBatch}>Upload Another Batch</button>
                <button className="btn btn-primary btn-sm" onClick={() => navigate("/documents")}>View All Documents →</button>
              </div>
            )}
          </div>
        )}

        {conflictFiles.length > 0 && (
          <div style={{ padding: "14px 16px", background: "rgba(234,179,8,0.08)", borderRadius: 8, border: "1px solid rgba(234,179,8,0.3)", fontSize: 13, color: "var(--text-primary)", marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#b45309" }}>⚠ Files already processing</div>
            <div style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text-secondary)", marginBottom: 10 }}>
              {conflictFiles.map(f => <div key={f}>· {f}</div>)}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10 }}>
              These files are currently processing or were recently completed. Uploading them again will restart processing and overwrite existing results.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => { setConflictFiles([]); handleBatchUpload(true); }}>Upload Anyway</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setConflictFiles([])}>Cancel</button>
            </div>
          </div>
        )}

        {conflictFiles.length > 0 && (
          <div style={{ padding: "14px 16px", background: "rgba(234,179,8,0.08)", borderRadius: 8, border: "1px solid rgba(234,179,8,0.3)", fontSize: 13, color: "var(--text-primary)", marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#b45309" }}>⚠ Files already processing</div>
            <div style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text-secondary)", marginBottom: 10 }}>
              {conflictFiles.map(f => <div key={f}>· {f}</div>)}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10 }}>
              These files are currently processing or were recently completed. Uploading again will restart processing and overwrite existing results.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => { setConflictFiles([]); handleBatchUpload(true); }}>Upload Anyway</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setConflictFiles([])}>Cancel</button>
            </div>
          </div>
        )}

        {error && <div style={{ padding: "12px 14px", background: "rgba(239,68,68,0.08)", borderRadius: 8, border: "1px solid rgba(239,68,68,0.25)", fontSize: 13, color: "var(--error)", marginTop: 12 }}>Upload failed: {error}</div>}

        {!singleFiles.length && !batchFiles.length && batchPhase === "staging" && !trackedSingles.length && (
          <div style={{ marginTop: 18, padding: "14px 16px", background: "var(--blue-50)", border: "1px solid var(--blue-100)", borderRadius: 8, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.65 }}>
            <strong style={{ color: "var(--blue-700)" }}>Processing pipeline:</strong> Documents are extracted by Azure Document Intelligence, indexed to AI Search, and evaluated against your QA criteria using GPT-4.1. Processing takes 10–20 minutes per document. Documents appear in All Documents only after processing is fully complete.
          </div>
        )}
      </div>
    </>
  );
}